import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import JsonLd from '../components/JsonLd';
import {
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildWebPageSchema,
  buildSoftwareApplicationSchema,
  buildSchemaGraph,
  formatDescription,
} from '../utils/seo';
import Footer from '../Footer';
import './SeoLandingPage.css';

const APP_DOWNLOAD_URL = 'https://merra.onelink.me/uXXR/0vws5k9w';
const APP_DOWNLOAD_QR_SRC = `${process.env.PUBLIC_URL}/appsflyer-merra-0vws5k9w_qr_code.png`;

export default function SeoLandingPage({
  canonicalPath,
  title,
  description,
  h1,
  subheading,
  primaryCtaLabel = 'Try Merra AI free',
  promiseLine = 'Easier than writing a tweet.',
  mockDescriptionLabel = 'Description',
  mockDescriptionText = 'Merra AI turns raw clips into ready-to-post short-form content with scripts, voiceover, captions, music, and edits.',
  audienceTitle = 'Who this is for',
  audienceBullets = [],
  stepTitle = 'How it works (3 steps)',
  steps = [],
  examplesTitle = 'Examples',
  examples = [],
  testimonialTitle = 'What our users are saying',
  testimonials = [],
  seoArticleTitle,
  seoArticleParagraphs = [],
  featureBullets = [],
  sections = [],
  faqs = [],
  relatedLinks = [],
  keywords,
}) {
  const navigate = useNavigate();
  const canonicalUrl = `https://www.merra.ai${canonicalPath}`;
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
  const [isDownloadBannerDismissed, setIsDownloadBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem('downloadBannerDismissed') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const showMobileDownloadBanner = isMobile && !isDownloadBannerDismissed;

  const handleGoogleSignup = () => {
    const backendUrl =
      process.env.NODE_ENV === 'production'
        ? 'https://proto-backend-d2c1638486b9.herokuapp.com'
        : 'http://localhost:5000';
    window.location.href = `${backendUrl}/auth/google`;
  };

  const dismissDownloadBanner = () => {
    setIsDownloadBannerDismissed(true);
    try {
      localStorage.setItem('downloadBannerDismissed', 'true');
    } catch {}
  };

  const faqJsonLd =
    faqs && faqs.length
      ? {
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }
      : null;

  const pageSchema = buildSchemaGraph([
    buildOrganizationSchema(),
    buildWebSiteSchema(),
    buildWebPageSchema({
      name: title,
      description: formatDescription(description),
      url: canonicalUrl,
    }),
    buildSoftwareApplicationSchema({
      name: h1,
      description,
      url: canonicalUrl,
    }),
    faqJsonLd,
  ]);

  const wrapperClassName = useMemo(
    () => `seo-landing${showMobileDownloadBanner ? ' has-download-banner' : ''}`,
    [showMobileDownloadBanner]
  );

  return (
    <div className={wrapperClassName}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords ? <meta name="keywords" content={keywords} /> : null}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>
      <JsonLd schema={pageSchema} />

      {/* Desktop floating QR download widget */}
      <div className="download-qr-float" aria-hidden={isMobile}>
        <a
          href={APP_DOWNLOAD_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="download-qr-float__card"
          aria-label="Download Merra AI app"
        >
          <img src={APP_DOWNLOAD_QR_SRC} alt="Scan to download Merra AI" className="download-qr-float__img" loading="lazy" />
          <div className="download-qr-float__text">
            <div className="download-qr-float__title">Get Merra AI</div>
            <div className="download-qr-float__subtitle">Scan to get the app</div>
          </div>
        </a>
      </div>

      {/* Mobile download banner */}
      {showMobileDownloadBanner && (
        <div className="download-banner" role="region" aria-label="Download Merra AI app">
          <button type="button" className="download-banner__close" onClick={dismissDownloadBanner} aria-label="Dismiss download banner">
            ×
          </button>
          <div className="download-banner__content">
            <div className="download-banner__title">Download Merra AI</div>
            <div className="download-banner__subtitle">AI video editor on iPhone</div>
          </div>
          <a className="download-banner__cta" href={APP_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
            Download
          </a>
        </div>
      )}

      <div className="seo-landing__container">
        <div className="seo-landing__nav">
          <Link to="/landing-page" className="seo-landing__brand">
            Merra AI
          </Link>
          <div className="seo-landing__navlinks">
            <Link to="/blog" className="seo-landing__link" target="_blank" rel="noopener noreferrer">
              Blog
            </Link>
            <Link to="/about" className="seo-landing__link" target="_blank" rel="noopener noreferrer">
              About
            </Link>
          </div>
        </div>

        <div className="seo-landing__hero">
          <div className="seo-landing__heroCard">
            <h1 className="seo-landing__h1">{h1}</h1>
            <p className="seo-landing__sub">{subheading}</p>
            <div className="seo-landing__ctaRow">
              <button type="button" className="seo-landing__ctaPrimary" onClick={handleGoogleSignup}>
                {primaryCtaLabel}
              </button>
            </div>
            <div className="seo-landing__fineprint">First 3 videos are free. No credit card required.</div>
          </div>

          <div className="seo-landing__heroCard" aria-label="Key features">
            <ul className="seo-landing__featureList">
              {featureBullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="seo-landing__tagline">
          <div className="seo-landing__taglinePill">{promiseLine}</div>
        </div>

        {/* Landing-page interface demo */}
        <div style={{ marginTop: 18 }}>
          <div className="browser-mockup">
            <div className="browser-mockup-chrome">
              <div className="browser-dots">
                <span className="browser-dot browser-dot-red" aria-hidden="true" />
                <span className="browser-dot browser-dot-yellow" aria-hidden="true" />
                <span className="browser-dot browser-dot-green" aria-hidden="true" />
              </div>
              <div className="browser-url-bar">merra.ai/workspace</div>
              <div className="browser-fullscreen" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
              </div>
            </div>
            <div className="browser-mockup-content">
              <div className="input-output-container" style={{ position: 'relative' }}>
                {/* Inputs */}
                <div style={{ flex: '1', maxWidth: 300, width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#333', textAlign: 'left' }}>Step 1: Upload</h3>
                  <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 10, color: '#111' }}>1. Voice sample</div>
                    <audio controls style={{ width: '100%', height: 40 }}>
                      <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_bouki.mp3" type="audio/mpeg" />
                    </audio>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 10, color: '#111' }}>2. {mockDescriptionLabel}</div>
                    <div style={{ background: '#f3f4f6', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 10, padding: 12, fontSize: 13, lineHeight: 1.55, color: 'rgba(17,17,17,0.72)' }}>
                      {mockDescriptionText}
                    </div>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 10, color: '#111' }}>3. Raw clips</div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} style={{ width: 46, height: 46, borderRadius: 10, background: 'linear-gradient(135deg, rgba(171,88,152,0.22), rgba(92,75,211,0.18))', border: '1px solid rgba(0,0,0,0.06)' }} />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Output */}
                <div style={{ flex: '1', maxWidth: 300, width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#333', textAlign: 'left' }}>Output</h3>
                  <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 10, color: '#111' }}>Generated video</div>
                    <video
                      controls
                      preload="metadata"
                      playsInline
                      poster="/poster-for-example2.webp"
                      style={{
                        width: '100%',
                        borderRadius: 12,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        objectFit: 'cover',
                        aspectRatio: '9 / 16',
                        height: 'auto',
                        maxHeight: 320,
                        background: '#000',
                      }}
                    >
                      <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Example-New-GenerateVideoWithVoiceover-Merra-2.mp4" type="video/mp4" />
                    </video>
                    <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(17,17,17,0.6)', fontWeight: 800 }}>
                      Generate → review → regenerate (script, voiceover, editing)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {(steps?.length > 0 || audienceBullets?.length > 0) && (
          <div className="seo-landing__grid2">
            {steps?.length > 0 && (
              <div className="seo-landing__section">
                <div className="seo-landing__h2">{stepTitle}</div>
                <ol className="seo-landing__steps">
                  {steps.map((s) => (
                    <li key={s}>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {audienceBullets?.length > 0 && (
              <div className="seo-landing__section">
                <div className="seo-landing__h2">For who ?</div>
                <ul className="seo-landing__featureList">
                  {audienceBullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {true && (
          <div className="seo-landing__sections">
            <div className="seo-landing__section">
              <div className="seo-landing__h2">{examplesTitle}</div>
              <p className="seo-landing__p" style={{ marginTop: 10 }}>Made with Merra AI</p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '30px',
                  maxWidth: '100%',
                  marginTop: 14,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <video
                    src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Kims%20intro%20compressed.mp4"
                    preload="none"
                    controls
                    poster="/poster-mini-kim.webp"
                    style={{ width: '100%', maxHeight: '70vh', objectFit: 'cover', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                    playsInline
                  >
                    <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Kims%20intro%20compressed.mp4" type="video/mp4" />
                  </video>
                  <p style={{ margin: '8px 0 0 0', fontSize: 14, textAlign: 'center', color: '#333' }}>
                    <a href="https://www.instagram.com/keepitmoving.co/" target="_blank" rel="noopener noreferrer" style={{ color: '#333', textDecoration: 'none' }}>
                      @keepitmoving.co <span style={{ fontSize: 12, color: '#666' }}>(Dance therapy)</span>
                    </a>
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <video
                    src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/transpraia%20exhibition%20video.mp4"
                    preload="none"
                    controls
                    poster="/poster-mini-transpraia.webp"
                    style={{ width: '100%', maxHeight: '70vh', objectFit: 'cover', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                    playsInline
                  >
                    <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/transpraia%20exhibition%20video.mp4" type="video/mp4" />
                  </video>
                  <p style={{ margin: '8px 0 0 0', fontSize: 14, textAlign: 'center', color: '#333' }}>
                    <a href="https://www.instagram.com/p/DOgWmwhDCtt/" target="_blank" rel="noopener noreferrer" style={{ color: '#333', textDecoration: 'none' }}>
                      @transpraia <span style={{ fontSize: 12, color: '#666' }}>(Art exhibition)</span>
                    </a>
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <video
                    src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Amber%20intro.mp4"
                    preload="none"
                    controls
                    poster="/poster-mini-amber.webp"
                    style={{ width: '100%', maxHeight: '70vh', objectFit: 'cover', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                    playsInline
                  >
                    <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Amber%20intro.mp4" type="video/mp4" />
                  </video>
                  <p style={{ margin: '8px 0 0 0', fontSize: 14, textAlign: 'center', color: '#333' }}>
                    <a href="https://www.instagram.com/notonthe_feed/" target="_blank" rel="noopener noreferrer" style={{ color: '#333', textDecoration: 'none' }}>
                      @notOnThe_Feed <span style={{ fontSize: 12, color: '#666' }}>(Media)</span>
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="seo-landing__sections">
          {sections.map((s) => (
            <div key={s.title} className="seo-landing__section">
              <div className="seo-landing__h2">{s.title}</div>
              <p className="seo-landing__p">{s.body}</p>
            </div>
          ))}
        </div>

        {testimonials?.length > 0 && (
          <div className="seo-landing__sections">
            <div className="seo-landing__section">
              <div className="seo-landing__h2">{testimonialTitle}</div>
              <div className="seo-landing__testimonials">
                {testimonials.map((t) => (
                  <div key={t.quote} className="seo-landing__testimonialCard">
                    <div className="seo-landing__quote">“{t.quote}”</div>
                    {t.by ? <div className="seo-landing__by">— {t.by}</div> : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="seo-landing__sections">
          <div className="seo-landing__section seo-landing__pricing">
            <div className="seo-landing__h2">Pricing</div>
            <div className="seo-landing__pricingGrid">
              <div className="seo-landing__priceCard">
                <div className="seo-landing__priceName">Free</div>
                <div className="seo-landing__priceValue">$0</div>
                <div className="seo-landing__priceDesc">3 videos / month</div>
              </div>
              <div className="seo-landing__priceCard seo-landing__priceCardPopular">
                <div className="seo-landing__popularBadge">POPULAR</div>
                <div className="seo-landing__priceName">Creator</div>
                <div className="seo-landing__priceValue">$25</div>
                <div className="seo-landing__priceDesc">20 videos / month</div>
              </div>
              <div className="seo-landing__priceCard">
                <div className="seo-landing__priceName">Pro</div>
                <div className="seo-landing__priceValue">$99</div>
                <div className="seo-landing__priceDesc">150 videos / month</div>
              </div>
            </div>
          </div>
        </div>

        <div className="seo-landing__sections">
          <div className="seo-landing__section seo-landing__movement">
            <div className="seo-landing__h2">Join the movement</div>
            <p className="seo-landing__p">
              Creators and teams use Merra AI to publish more consistently without spending hours editing. Start free and
              ship your next video today.
            </p>
            <div className="seo-landing__ctaRow">
              <button type="button" className="seo-landing__ctaPrimary" onClick={handleGoogleSignup}>
                Get started free
              </button>
            </div>
          </div>
        </div>

        {faqs?.length > 0 && (
          <div className="seo-landing__sections">
            <div className="seo-landing__section">
              <div className="seo-landing__h2">FAQ</div>
              <div className="seo-landing__faq">
                {faqs.map((f) => (
                  <div key={f.q} className="seo-landing__faqItem">
                    <div className="seo-landing__faqQ">{f.q}</div>
                    <div className="seo-landing__faqA">{f.a}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {seoArticleTitle && seoArticleParagraphs.length > 0 && (
          <div className="seo-landing__sections">
            <div className="seo-landing__section">
              <div className="seo-landing__h2">{seoArticleTitle}</div>
              {seoArticleParagraphs.map((p) => (
                <p key={p} className="seo-landing__p">
                  {p}
                </p>
              ))}
            </div>
          </div>
        )}

        {relatedLinks?.length > 0 && (
          <div className="seo-landing__sections">
            <div className="seo-landing__section">
              <div className="seo-landing__h2">Related use cases</div>
              <div className="seo-landing__related">
                {relatedLinks.map((l) => (
                  <Link key={l.to} to={l.to} className="seo-landing__relatedLink">
                    {l.label} →
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="seo-landing__footer">
          <div>© {new Date().getFullYear()} Merra.ai</div>
          <div>
            Want the iPhone app?{' '}
            <a href="https://merra.onelink.me/uXXR/0vws5k9w" target="_blank" rel="noopener noreferrer">
              Download Merra AI
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

